<template>
  <Card class="trip-timeline">
    <h1 class="trip-timeline__title">Your journey to departure</h1>
    <template v-if="desktop">
      <div class="trip-timeline__grid line" :class="{ 'no-cancellation-deadline': !hasCancellationDeadline }">
        <div class="trip-timeline__item__content">
          <div class="custom-icon-wrapper">
            <Globe class="custom-icon" />
          </div>
        </div>
        <div class="trip-timeline__item__content">
          <i class="custom-icon-wrapper icon-check-circle-1 green"></i>
        </div>
        <div class="trip-timeline__item__content" v-if="hasCancellationDeadline">
          <i class="custom-icon-wrapper icon-time-clock-circle yellow"></i>
        </div>
        <div class="trip-timeline__item__content">
          <div class="custom-icon-wrapper">
            <Confetti class="custom-icon" />
          </div>
        </div>
      </div>
      <div class="trip-timeline__grid" :class="{ 'no-cancellation-deadline': !hasCancellationDeadline }">
        <div class="trip-timeline__item__content">
          <span>Launch itinerary</span>
          <span class="grey">Today</span>
        </div>
        <div class="trip-timeline__item__content">
          <span>First booking</span>
          <span class="grey" v-if="timelines?.firstBookingDeadline">Target: {{ timelines?.firstBookingDeadline }}</span>
          <span class="trip-timeline__item__content__explanation green"
            >Trip logistics confirmed after first booking.</span
          >
        </div>
        <div class="trip-timeline__item__content" v-if="hasCancellationDeadline">
          <span>Cancel deadline</span>
          <span class="grey" v-if="hasCancellationDeadline">{{ timelines?.cancellationDeadline }}</span>
          <span class="trip-timeline__item__content__explanation yellow">Cancel risk free until this date.</span>
        </div>
        <div class="trip-timeline__item__content">
          <span>Departure</span>
          <span class="grey" v-if="timelines?.tripStartDate">{{ timelines?.tripStartDate }}</span>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="trip-timeline__flex" :class="{ 'no-cancellation-deadline': !hasCancellationDeadline }">
        <div class="trip-timeline__item__content">
          <div class="custom-icon-wrapper">
            <Globe class="custom-icon" />
          </div>
          <span class="title">Launch itinerary</span>
          <span class="grey">Today</span>
        </div>
        <div class="trip-timeline__item__content">
          <i class="custom-icon-wrapper icon-check-circle-1 green"></i>
          <span class="title">First booking</span>
          <span class="grey" v-if="timelines?.firstBookingDeadline">Target: {{ timelines?.firstBookingDeadline }}</span>
          <span class="trip-timeline__item__content__explanation green"
            >Trip logistics confirmed after first booking.</span
          >
        </div>
        <div class="trip-timeline__item__content" v-if="hasCancellationDeadline">
          <i class="custom-icon-wrapper icon-time-clock-circle yellow"></i>
          <span class="title">Cancel deadline</span>
          <span class="grey" v-if="hasCancellationDeadline">{{ timelines?.cancellationDeadline }}</span>
          <span class="trip-timeline__item__content__explanation yellow">Cancel risk free until this date.</span>
        </div>
        <div class="trip-timeline__item__content">
          <div class="custom-icon-wrapper">
            <Confetti class="custom-icon" />
          </div>
          <span class="title">Departure</span>
          <span class="grey" v-if="timelines?.tripStartDate">{{ timelines?.tripStartDate }}</span>
        </div>
      </div>
    </template>
  </Card>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import Card from '@/components/Theme2/Card.vue'
import Confetti from 'app/assets/images/self-serve/confetti.svg?component'
import Globe from 'app/assets/images/explore/globe.svg?component'
import { useTypedItineraryStore } from 'app/javascript/src/stores/typed/useTypedItineraryStore'
import Product from '@/models/product'
import moment from 'moment'
import { DateFormat } from 'app/javascript/src/utils/dateUtils'
import { useResponsive } from '@/helpers/vueHelpers.js'

const store = useTypedItineraryStore<Product>()
const { desktop } = useResponsive()

const timelines = computed(() => {
  const timelines = store.data?.timelines
  return {
    firstBookingDeadline: timelines?.firstBookingDeadline
      ? moment(timelines?.firstBookingDeadline).format(DateFormat.SHORT)
      : null,
    cancellationDeadline: timelines?.cancellationDeadline
      ? moment(timelines?.cancellationDeadline).format(DateFormat.SHORT)
      : null,
    tripStartDate: timelines?.tripStartDate ? moment(timelines?.tripStartDate).format(DateFormat.SHORT) : null,
  }
})
const hasCancellationDeadline = computed(() => timelines.value?.cancellationDeadline)
</script>

<style lang="scss" scoped>
.trip-timeline {
  display: flex;
  flex-direction: column;
  gap: $spacing-4;

  .trip-timeline__title {
    font-size: $text-xl;
    margin-block: 0;
  }

  .trip-timeline__grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    position: relative;
    gap: $spacing-4;

    &.no-cancellation-deadline {
      grid-template-columns: 1fr 1fr 1fr;
    }

    &.line::before {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      height: 2px;
      background-color: $grey-50;
    }

    .trip-timeline__item__content {
      @include flex-column($spacing-2);
      align-items: center;
      z-index: 1;
      text-align: center;

      span:first-child {
        font-weight: $font-semibold;
      }
    }
  }

  .trip-timeline__flex {
    @include flex-column($spacing-4);
    position: relative;

    .trip-timeline__item__content {
      @include flex-column($spacing-2);
      text-align: center;
      border: 1px solid $grey-50;
      padding: $spacing-4;
      border-radius: $rounded-2xl;
      align-items: center;
      position: relative;

      .title {
        font-weight: $font-semibold;
      }

      &:not(:last-child)::after {
        content: '';
        position: absolute;
        bottom: -$spacing-4;
        left: 50%;
        transform: translateX(-50%);
        width: 2px;
        height: $spacing-4;
        background-color: $grey-50;
      }
    }
  }

  .custom-icon-wrapper {
    width: 40px;
    height: 40px;
    background-color: $grey-25;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .custom-icon,
  .icon-time-clock-circle::before,
  .icon-check-circle-1::before {
    width: 18px;
    height: 18px;
    font-size: 18px;
  }

  .trip-timeline__item__content__explanation {
    font-size: $text-sm;
    text-align: center;
    padding: $spacing-2;
    border-radius: $rounded-xl;
  }

  .yellow {
    background-color: $yellow-50;
    color: $yellow-500;
  }

  .green {
    background-color: $green-50;
    color: $green-300;
  }

  .grey {
    color: $grey-100;
  }
}
</style>
